import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';

export interface RoamingQuoteResponse extends Record<string, unknown> {
  roaming_limit_amount?: number | string | null;
  roaming_mobile_number?: string | null;
  currency_code?: string | null;
  currency_label?: string | null;
  currency?: string | null;
  roaming_currency_code?: string | null;
  roaming_currency_label?: string | null;
}

interface UseRoamingQuoteParams {
  initiatorId?: number;
  startDate?: string | null;
  endDate?: string | null;
  enabled?: boolean;
}

export function useRoamingQuote({
  initiatorId,
  startDate,
  endDate,
  enabled = true,
}: UseRoamingQuoteParams) {
  return useQuery<RoamingQuoteResponse, Error>({
    queryKey: [
      'trip-form',
      'roaming-request',
      initiatorId ?? null,
      startDate ?? null,
      endDate ?? null,
    ],
    enabled: Boolean(enabled && initiatorId && startDate && endDate),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!initiatorId || !startDate || !endDate) {
        throw new Error('Missing roaming quote parameters');
      }

      const response = await apiFetch('/api/v1/business-trip-requests/roaming-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          initiator_id: initiatorId,
          start_date: startDate,
          end_date: endDate,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | RoamingQuoteResponse
        | { error?: { message?: unknown } }
        | null;

      if (!response.ok || !payload || typeof payload !== 'object') {
        throw new Error('Failed to fetch roaming details');
      }

      if ('error' in payload && payload.error && typeof payload.error === 'object') {
        const maybeMessage = (payload.error as { message?: unknown }).message;
        if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
          throw new Error(maybeMessage.trim());
        }
        throw new Error('Failed to fetch roaming details');
      }

      return payload as RoamingQuoteResponse;
    },
  });
}
